<script>
  import { scale } from 'svelte/transition';
  import { backOut, expoIn } from 'svelte/easing';
  import {
    windows,
    activeId,
    closeWindow,
    minimizeWindow,
    zoomWindow,
    enterFullscreen,
    focusWindow,
    moveWindow,
    resizeWindow,
  } from '../os/windows.svelte.js';

  let { win, active = false, children } = $props();

  let dragging = $state(false);
  let startX = 0;
  let startY = 0;
  let originX = 0;
  let originY = 0;
  let resizing = $state(false);
  let resizeDir = '';
  let originW = 0;
  let originH = 0;

  const resizeHandles = ['n', 'e', 's', 'w', 'ne', 'se', 'sw', 'nw'];

  function startDrag(e) {
    // Don't start a drag when the press begins on the traffic lights.
    if (e.target.closest('.traffic')) return;
    if (e.target.closest('.resize-handle')) return;
    // Don't allow dragging in fullscreen or split mode
    if (win.fullscreen || win.splitSide) return;
    focusWindow(win.id);
    dragging = true;
    startX = e.clientX;
    startY = e.clientY;
    originX = win.x;
    originY = win.y;
    globalThis.addEventListener('pointermove', onMove);
    globalThis.addEventListener('pointerup', endDrag);
  }

  function onMove(e) {
    if (!dragging) return;
    moveWindow(win.id, originX + (e.clientX - startX), originY + (e.clientY - startY));
  }

  function endDrag() {
    dragging = false;
    globalThis.removeEventListener('pointermove', onMove);
    globalThis.removeEventListener('pointerup', endDrag);
  }

  function startResize(e, dir) {
    e.stopPropagation();
    e.preventDefault();
    focusWindow(win.id);
    resizing = true;
    resizeDir = dir;
    startX = e.clientX;
    startY = e.clientY;
    originX = win.x;
    originY = win.y;
    originW = win.w;
    originH = win.h ?? e.currentTarget.closest('.window')?.getBoundingClientRect().height ?? win.minH ?? 260;
    globalThis.addEventListener('pointermove', onResize);
    globalThis.addEventListener('pointerup', endResize);
  }

  function onResize(e) {
    if (!resizing) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    const minW = win.minW ?? 320;
    const minH = win.minH ?? 220;

    let x = originX;
    let y = originY;
    let w = originW;
    let h = originH;

    if (resizeDir.includes('e')) w = originW + dx;
    if (resizeDir.includes('s')) h = originH + dy;
    if (resizeDir.includes('w')) {
      w = originW - dx;
      x = originX + dx;
      if (w < minW) {
        w = minW;
        x = originX + originW - minW;
      }
    }
    if (resizeDir.includes('n')) {
      h = originH - dy;
      y = originY + dy;
      if (h < minH) {
        h = minH;
        y = originY + originH - minH;
      }
    }

    resizeWindow(win.id, { x, y, w, h });
  }

  function endResize() {
    resizing = false;
    resizeDir = '';
    globalThis.removeEventListener('pointermove', onResize);
    globalThis.removeEventListener('pointerup', endResize);
  }
</script>

<!--
  A window is a custom, pointer-driven widget. The pointerdown handlers are
  pointer-only enhancements (bring-to-front + drag) with no keyboard equivalent
  required, so we knowingly opt out of the static-interaction a11y rule here.
-->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="window"
  data-window-id={win.id}
  class:inactive={!active}
  class:finder-window={win.appId === 'projects'}
  class:dragging={dragging}
  class:resizing={resizing}
  class:win-fullscreen={win.fullscreen}
  class:win-split-left={win.splitSide === 'left'}
  class:win-split-right={win.splitSide === 'right'}
  style="left:{win.x}px; top:{win.y}px; width:{win.w}px; {win.h ? `height:${win.h}px;` : ''} z-index:{win.z};"
  role="dialog"
  aria-label={win.title}
  tabindex="-1"
  onpointerdown={() => focusWindow(win.id)}
  in:scale={{ start: 0.85, duration: 400, easing: backOut }}
  out:scale={{ start: 0.95, duration: 150, easing: expoIn }}
>
  <!-- svelte-ignore a11y_no_static_element_interactions -->
  <div class="titlebar" onpointerdown={startDrag} ondblclick={() => { if (!win.fullscreen) zoomWindow(win.id); }}>
    <div class="traffic">
      <button
        class="tl close"
        aria-label="Close window"
        onclick={(e) => { e.stopPropagation(); closeWindow(win.id); }}
      ></button>
      <button
        class="tl min"
        aria-label="Minimize window"
        onclick={(e) => { e.stopPropagation(); minimizeWindow(win.id); }}
      ></button>
      <button class="tl zoom" aria-label={win.fullscreen ? 'Exit full screen' : 'Full screen'}
        title={win.fullscreen ? 'Exit Full Screen' : 'Enter Full Screen'}
        onclick={(e) => { e.stopPropagation(); enterFullscreen(win.id); }}>
      </button>
    </div>
    <span class="title">
      {#if win.appId === 'projects'}
        <span class="title-proxy">
          <svg viewBox="0 0 16 14" width="13" height="11" aria-hidden="true">
            <path d="M1.5 2.5 C1.5 1.7 2.2 1 3 1 h3.5 c.6 0 1.1.3 1.4.7 L9 3 h5 c.9 0 1.5.7 1.5 1.5 v7 c0 .9-.7 1.5-1.5 1.5 H3 c-.9 0-1.5-.7-1.5-1.5 z" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>
          </svg>
        </span>
      {/if}
      <span>{win.title}</span>
      {#if win.fullscreen}<span class="fullscreen-label">Full Screen · Esc to exit</span>{/if}
    </span>
  </div>

  <div class="body" class:body-flush={win.contentInset === 'none'}>
    {@render children?.()}
  </div>

  {#each resizeHandles as dir}
    {#if !win.fullscreen && !win.splitSide}
      <button
        class="resize-handle resize-{dir}"
        aria-label="Resize window"
        onpointerdown={(e) => startResize(e, dir)}
      ></button>
    {/if}
  {/each}
</div>

<style>
  .window {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    min-width: 240px;
    background: var(--window-bg);
    border: 2px solid;
    border-color: var(--bevel-light, #8b9694) var(--bevel-dark, #1a201f) var(--bevel-dark, #1a201f) var(--bevel-light, #8b9694);
    outline: 1px solid var(--bevel-outline, #000);
    border-radius: 0;
    box-shadow: 
      inset 1px 1px 0 rgba(255, 255, 255, 0.2),
      inset -1px -1px 0 rgba(0, 0, 0, 0.3),
      0 18px 42px rgba(0, 0, 0, 0.48), 0 2px 7px rgba(0, 0, 0, 0.35);
    transition: opacity 0.2s cubic-bezier(0.25, 0.8, 0.25, 1), transform 0.2s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  }
  .window.inactive { box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3); }
  .window.dragging {
    opacity: 0.94;
    transform: scale(1.008);
    box-shadow: 0 26px 58px rgba(0, 0, 0, 0.52), 0 6px 16px rgba(0, 0, 0, 0.38);
  }
  .window.finder-window {
    min-width: min(560px, calc(100vw - 24px));
    min-height: min(360px, calc(100vh - 42px));
    border: 1px solid var(--window-border);
    background: var(--window-bg);
    border-radius: 0;
    box-shadow: 
      inset 1px 1px 0 rgba(255, 255, 255, 0.2),
      inset -1px -1px 0 rgba(0, 0, 0, 0.3),
      0 12px 28px rgba(0,0,0,0.42), 0 1px 4px rgba(0,0,0,0.35);
  }

  .titlebar {
    position: relative;
    display: flex;
    align-items: center;
    height: 22px;
    padding: 0 7px;
    cursor: default;
    background:
      repeating-linear-gradient(0deg, rgba(255,255,255,.035) 0 1px, rgba(0,0,0,.045) 1px 2px),
      linear-gradient(to bottom, #6b7272 0%, #4a5050 42%, #343a3a 100%);
    border-top: 1px solid rgba(255,255,255,0.28);
    border-bottom: 3px solid #0b0c0b;
    border-radius: 0;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.4),
      inset 0 2px 0 rgba(255, 255, 255, 0.16),
      inset 0 -3px 2px rgba(0, 0, 0, 0.42),
      0 2px 0 rgba(0, 0, 0, 0.35);
  }
  .window.inactive .titlebar {
    background:
      repeating-linear-gradient(0deg, rgba(255,255,255,.02) 0 1px, rgba(0,0,0,.035) 1px 2px),
      linear-gradient(to bottom, #4b5151 0%, #333939 55%, #292e2e 100%);
    border-top-color: rgba(255,255,255,0.1);
    border-bottom-width: 2px;
    border-bottom-color: #17191a;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.12),
      inset 0 -1px 0 rgba(0, 0, 0, 0.28);
  }
  .finder-window .titlebar {
    height: 26px;
    background:
      repeating-linear-gradient(0deg, rgba(255,255,255,.035) 0 1px, rgba(0,0,0,.045) 1px 2px),
      linear-gradient(to bottom, #6b7272 0%, #4a5050 42%, #343a3a 100%);
    border-top: 1px solid rgba(255,255,255,0.28);
    border-bottom: 3px solid #0b0c0b;
    border-radius: 0;
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.4),
      inset 0 2px 0 rgba(255, 255, 255, 0.16),
      inset 0 -3px 2px rgba(0, 0, 0, 0.42),
      0 2px 0 rgba(0, 0, 0, 0.35);
  }
  .finder-window .title {
    font-size: 12px;
    color: #f3f6f8;
    opacity: 1;
    text-shadow: none;
  }
  .finder-window .traffic {
    gap: 7px;
    opacity: 1;
  }
  .finder-window .tl {
    height: 13px;
    width: 13px;
  }
  .title {
    position: absolute;
    inset: 0 76px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
    color: #f1f4f7;
    text-shadow: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .fullscreen-label {
    margin-left: 8px;
    color: #aeb8bb;
    font-size: 9px;
    font-weight: 500;
  }
  .title-proxy {
    display: inline-grid;
    place-items: center;
    width: 13px;
    height: 13px;
    transform: translateY(-1px);
  }
  .title-proxy :global(.os-icon) {
    width: 13px;
    height: 13px;
  }
  .window.inactive .title { color: #aeb6bf; }

  .traffic {
    position: relative;
    z-index: 1;
    display: flex;
    gap: 6px;
    align-items: center;
  }
  .tl {
    position: relative;
    width: 13px;
    height: 13px;
    padding: 0;
    display: grid;
    place-items: center;
    border: 1px solid rgba(0, 0, 0, 0.4);
    border-top-color: rgba(255, 255, 255, 0.55);
    border-radius: 50%;
    cursor: pointer;
    transition: transform 0.15s cubic-bezier(0.25, 0.8, 0.25, 1.4), box-shadow 0.12s ease, background 0.15s ease, border-color 0.15s ease;
  }
  .traffic:hover .tl:hover {
    transform: scale(1.22);
  }
  .tl:active {
    transform: translateY(1px) scale(0.94) !important;
    filter: brightness(0.82);
    box-shadow:
      inset 0 1px 2px rgba(0, 0, 0, 0.55),
      inset 0 -1px 0 rgba(255, 255, 255, 0.15) !important;
  }
  .tl::after {
    display: none;
  }
  .tl::before {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    font-size: 9px;
    font-weight: 800;
    line-height: 1;
    color: rgba(25, 29, 33, 0.82);
    opacity: 0.72;
    transition: opacity 0.15s ease-in-out;
  }
  .tl.close::before { content: "\00d7"; }
  .tl.min::before   { content: "\2013"; }
  .tl.zoom::before  { content: "+"; }
  .tl.close {
    background: linear-gradient(to bottom, #ff7b72, #d9403b);
    box-shadow: inset 0 1px 1px rgba(255,255,255,0.5), inset 0 -1px 1px rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.5), 0 2px 3px rgba(0,0,0,0.3);
  }
  .tl.min {
    background: linear-gradient(to bottom, #ffd264, #d99a1f);
    box-shadow: inset 0 1px 1px rgba(255,255,255,0.5), inset 0 -1px 1px rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.5), 0 2px 3px rgba(0,0,0,0.3);
  }
  .tl.zoom {
    background: linear-gradient(to bottom, #6ee17b, #24a83e);
    box-shadow: inset 0 1px 1px rgba(255,255,255,0.5), inset 0 -1px 1px rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.5), 0 2px 3px rgba(0,0,0,0.3);
  }

  .traffic:hover .tl::before { opacity: 0.95; }
  .window.inactive .tl { background: #737b84; border-color: rgba(0, 0, 0, 0.28); box-shadow: none; }
  .window.inactive .tl::after { display: none; }

  .body {
    flex: 1;
    min-height: 0;
    background: var(--window-bg);
    padding: 16px 18px;
    overflow: auto;
    user-select: text;
  }

  .body.body-flush {
    padding: 0;
    overflow: hidden;
  }

  .resize-handle {
    position: absolute;
    z-index: 6;
    padding: 0;
    background: transparent;
    border: 0;
    opacity: 0;
  }
  .resize-n,
  .resize-s {
    left: 8px;
    right: 8px;
    height: 7px;
    cursor: ns-resize;
  }
  .resize-n { top: -3px; }
  .resize-s { bottom: -3px; }
  .resize-e,
  .resize-w {
    top: 8px;
    bottom: 8px;
    width: 7px;
    cursor: ew-resize;
  }
  .resize-e { right: -3px; }
  .resize-w { left: -3px; }
  .resize-ne,
  .resize-se,
  .resize-sw,
  .resize-nw {
    width: 14px;
    height: 14px;
  }
  .resize-ne { top: -4px; right: -4px; cursor: nesw-resize; }
  .resize-se { right: -4px; bottom: -4px; cursor: nwse-resize; }
  .resize-sw { left: -4px; bottom: -4px; cursor: nesw-resize; }
  .resize-nw { top: -4px; left: -4px; cursor: nwse-resize; }
  .finder-window .body {
    flex: 1;
    min-height: 0;
    padding: 0;
    overflow: hidden;
    background: #1a1f28;
    border-radius: 0 0 6px 6px;
  }
  .window.resizing {
    user-select: none;
  }
  .window.resizing .body,
  .window.dragging .body {
    pointer-events: none;
  }
  .win-fullscreen {
    border-radius: 0 !important;
    border: none !important;
    box-shadow: none !important;
    overflow: hidden;
  }
  .win-fullscreen .titlebar {
    border-radius: 0 !important;
    margin-top: 0;
    opacity: 1;
    z-index: 1;
  }
  .win-split-left,
  .win-split-right {
    border-radius: 0 !important;
    box-shadow: none !important;
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .win-split-left { border-right: 2px solid #444 !important; }
  .win-split-right { border-left: 2px solid #444 !important; }

  @media (prefers-reduced-motion: reduce) {
    .tl { transition: background 0.15s ease, border-color 0.15s ease; }
    .tl:active { transform: translateY(1px) !important; }
  }
</style>
